import { collection, doc, getDoc, getDocs } from 'firebase/firestore';

/**
 * Normalizes current_period_end from Firestore (Timestamp, ISO string, Unix seconds/ms).
 * @param {unknown} raw
 * @returns {Date|null}
 */
export function parseSubscriptionPeriodEnd(raw) {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw;
  }
  if (typeof raw === 'object' && raw !== null && typeof raw.toDate === 'function') {
    try {
      const d = raw.toDate();
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'number') {
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof raw === 'string') {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

const GRANTING_STATUSES = new Set(['active', 'trialing', 'past_due']);

/**
 * Pro subscription price IDs — must stay in sync with the server-side set in
 * `functions/tokenPurchase.ts` (SUBSCRIPTION_PRICE_IDS).
 *
 * Used as a fallback so an in-period subscription on a recognized price still
 * grants premium even if `items[0].plan.metadata.firebaseRole` was wiped
 * (e.g. by a prior webhook sync that didn't know about the price ID yet).
 * NEVER remove legacy IDs — subscribers keep renewing on the price they
 * originally signed up for.
 */
const PREMIUM_PRICE_IDS = new Set([
  // Current
  'price_1Tvmh6H9z0ThRFdKBMzLb7U2', // monthly ($7.99)
  'price_1TvmkOH9z0ThRFdK3lOpZODp', // annual ($67.00)
  // Legacy (do not remove)
  'price_1TZhIVH9z0ThRFdKjTFlKzte', // monthly ($9.99)
  'price_1TZhJOH9z0ThRFdKwLK9KJV7', // annual ($79.99)
  'price_1TcSq4H9z0ThRFdKDXtsNDOx', // monthly ($5.99)
  'price_1TcSqeH9z0ThRFdKeOs0HGnO', // annual ($49.00)
  'price_1RQWvZH9z0ThRFdK7wtNgyUf',
  'price_1RJgJVH9z0ThRFdKmQP4aZUJ',
  'price_1RJgKPH9z0ThRFdKWSNYFlBk',
  'price_1RFEs4H71ezeGn9C92kBLPYE',
  'price_1RFEtbH71ezeGn9CJnCUQGnI',
  'price_1RGkQUH71ezeGn9CIY9C9ZDo',
]);

/**
 * Whether a user's subscription document currently grants Pro access (paid-through period end).
 *
 * Two writers can produce this doc and they use different schemas:
 *   1. The official `firestore-stripe-payments` Firebase Extension writes
 *      `role: 'premium'` at the TOP LEVEL of the doc (mapped from
 *      `firebaseRole` metadata on the Stripe PRODUCT).
 *   2. Our custom webhook in `functions/tokenPurchase.ts` writes
 *      `items[0].plan.metadata.firebaseRole = 'premium'` (mapped from a
 *      hard-coded price ID allowlist or Stripe price/product metadata).
 *
 * The two compete on every Stripe event; this function accepts any of the
 * recognized signals so whichever writer ran last, premium is still granted.
 *
 * @param {import('firebase/firestore').DocumentData} subscription
 * @param {Date} [now]
 * @returns {boolean}
 */
export function subscriptionDocGrantsPremiumAccess(subscription, now = new Date()) {
  if (!subscription || typeof subscription !== 'object') return false;

  const item = subscription.items?.[0];
  const plan = item?.plan;
  const price = item?.price;

  // Signal A: Stripe Extension top-level role field
  const grantedByTopLevelRole = subscription.role === 'premium';

  // Signal B: items[0].plan.metadata.firebaseRole (our custom webhook schema)
  const grantedByPlanMetadata = plan?.metadata?.firebaseRole === 'premium';

  // Signal C: items[0].price.product.metadata.firebaseRole (extension expanded shape)
  const productMeta =
    (price && typeof price.product === 'object' && price.product?.metadata) ||
    (plan && typeof plan.product === 'object' && plan.product?.metadata) ||
    null;
  const grantedByProductMetadata = productMeta?.firebaseRole === 'premium';

  // Signal D: recognized Pro price ID (fallback for legacy / broken docs)
  const priceIdCandidates = [plan?.id, price?.id, item?.id].filter(
    (id) => typeof id === 'string'
  );
  const grantedByPriceId = priceIdCandidates.some((id) => PREMIUM_PRICE_IDS.has(id));

  const grantedBySomeRole =
    grantedByTopLevelRole ||
    grantedByPlanMetadata ||
    grantedByProductMetadata ||
    grantedByPriceId;
  if (!grantedBySomeRole) return false;

  const periodEnd = parseSubscriptionPeriodEnd(subscription.current_period_end);
  if (!periodEnd || periodEnd.getTime() <= now.getTime()) return false;

  const status = subscription.status;
  if (status === 'canceled') return true;
  if (GRANTING_STATUSES.has(status)) return true;
  return false;
}

/**
 * Whether a user's partner bootcamp grant currently grants Pro access.
 * @param {unknown} partnerProUntil
 * @param {Date} [now]
 * @returns {boolean}
 */
export function partnerProUntilGrantsPremiumAccess(partnerProUntil, now = new Date()) {
  const end = parseSubscriptionPeriodEnd(partnerProUntil);
  return end != null && end.getTime() > now.getTime();
}

/**
 * Resolve premium access from Firestore (Stripe subscription and/or partner grant).
 * Use anywhere that previously only scanned `users/{uid}/subscriptions`.
 *
 * @param {import('firebase/firestore').Firestore} firestoreDb
 * @param {string} userId
 * @param {Date} [now]
 */
export async function fetchUserPremiumAccess(firestoreDb, userId, now = new Date()) {
  const emptySubscriptionData = {
    id: null,
    cancelAtPeriodEnd: false,
    currentPeriodEnd: null,
    status: null,
  };

  if (!userId) {
    return {
      isPremium: false,
      hasPaidSubscription: false,
      partnerProUntil: null,
      planType: null,
      subscriptionData: emptySubscriptionData,
    };
  }

  let isPremium = false;
  let hasPaidSubscription = false;
  let partnerProUntil = null;
  let planType = null;
  let subscriptionData = emptySubscriptionData;

  const userSnap = await getDoc(doc(firestoreDb, 'users', userId));
  if (userSnap.exists()) {
    const userData = userSnap.data();
    if (partnerProUntilGrantsPremiumAccess(userData?.partnerProUntil, now)) {
      isPremium = true;
      partnerProUntil = parseSubscriptionPeriodEnd(userData.partnerProUntil);
    }
  }

  const subscriptionsSnapshot = await getDocs(
    collection(firestoreDb, `users/${userId}/subscriptions`)
  );

  for (const docSnapshot of subscriptionsSnapshot.docs) {
    const subscription = docSnapshot.data();
    if (!subscriptionDocGrantsPremiumAccess(subscription, now)) continue;

    hasPaidSubscription = true;
    isPremium = true;

    const firstItem = subscription.items?.[0];
    const interval =
      firstItem?.plan?.interval ||
      firstItem?.price?.recurring?.interval ||
      null;
    const planTypeMeta =
      firstItem?.plan?.metadata?.type ||
      firstItem?.price?.metadata?.type ||
      null;
    planType = interval === 'year' || planTypeMeta === 'annual' ? 'annual' : 'monthly';

    subscriptionData = {
      id: docSnapshot.id,
      cancelAtPeriodEnd: subscription.cancel_at_period_end || false,
      currentPeriodEnd: parseSubscriptionPeriodEnd(subscription.current_period_end),
      status: subscription.status,
    };
    break;
  }

  return {
    isPremium,
    hasPaidSubscription,
    partnerProUntil,
    planType,
    subscriptionData,
  };
}
